import { MIN_ORG_GOALS, parseKraSetCsv, validateKraSet, type ImportedKraSet } from '@cfl/shared';
import { Layers, Plus, X } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  CardContent,
  Combobox,
  Input,
  Label,
  toast,
} from '@/components/cargofl-ui';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { BulkImportButton, BulkImportDialog } from '@/components/bulk-import-dialog';
import { LibraryList } from '@/components/kra/library-list';
import { useKraPermissions } from '@/components/kra/permissions';
import { StatTile } from '@/components/stat-tile';
import { Hint } from '@/components/cargofl-ui';
import { PILL_CLASS } from '@/lib/badges';
import { useCreateKraGroup, useKraGroups, type KraGroup } from '@/lib/kra-groups-api';
import {
  useCreateKraSet,
  useDeleteKraSet,
  useKraSets,
  useUpdateKraSet,
  type AssignOutcome,
  type KraSet,
} from '@/lib/kra-sets-api';
import {
  flattenDepartments,
  useDepartments,
  useDesignations,
  type Designation,
} from '@/lib/people-api';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

const ORG_WIDE = '__org__';
const NEW = '__new__';

/**
 * What a save assigned, in one line.
 *
 * "already had goals" is the normal steady state on a re-save, not a failure: assigning
 * only ever creates, so everyone who got this group last time comes back skipped. An
 * error per person every time somebody renames a group would train people to ignore them.
 */
function autoAssignNote(outcomes: AssignOutcome[]): string | undefined {
  if (outcomes.length === 0) return undefined;
  const assigned = outcomes.filter((o) => o.assigned).length;
  const kept = outcomes.filter(
    (o) => o.reason === 'HAS_GOALS' || o.reason === 'ALREADY_ACCEPTED',
  ).length;
  const failed = outcomes.length - assigned - kept;

  const parts = [`Assigned to ${assigned} of ${outcomes.length} holding these job titles`];
  if (kept > 0) parts.push(`${kept} already had goals and were left untouched`);
  if (failed > 0) parts.push(`${failed} could not be assigned`);
  return parts.join(' · ');
}

/**
 * Marks a dropdown value as a designation rather than an existing KRA id. Prefixed so it
 * can never collide with a cuid, which is what a real KRA id looks like.
 */
const DESIGNATION_PREFIX = 'designation:';

/**
 * Create Group.
 *
 * A group is a named weighting of KRAs — "Technical Architect" holding ten KRAs that
 * total exactly 100%. It is the stencil for an employee's whole organization goal set,
 * which is why the exact-100 rule that governs that set governs this: a group saved at
 * 95% would assign a set nobody could accept, and the missing 5% would only surface
 * later. Save stays disabled until the total lands on 100.
 */
export function KraSetTab() {
  const { data, isPending, error } = useKraSets();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  /** Pre-fills the name when a new group is started from the search box. */
  const [draftName, setDraftName] = React.useState('');
  const [importing, setImporting] = React.useState(false);
  const remove = useDeleteKraSet();
  const perms = useKraPermissions();

  function startNew(name: string) {
    setDraftName(name);
    setSelectedId(NEW);
  }

  const sets = data ?? [];
  const selected = selectedId === NEW ? null : (sets.find((s) => s.id === selectedId) ?? null);
  const editorOpen = selectedId !== null && (selectedId === NEW || selected !== null);
  const balanced = sets.filter((s) => s.totalWeightage === 100).length;

  /** Held between the bin icon and the confirmation. */
  const [deleting, setDeleting] = React.useState<{ id: string; name: string } | null>(null);

  async function onDelete(item: { id: string; name: string }) {
    try {
      await remove.mutateAsync(item.id);
      if (selectedId === item.id) setSelectedId(null);
      toast.success(`Deleted "${item.name}"`);
    } catch (e) {
      toast.error('Could not delete that group', { description: err(e) });
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatTile
          label="Groups"
          value={isPending ? '—' : sets.length}
          caption={sets.length === 0 ? 'none defined yet' : 'weighted KRA sets'}
        />
        <StatTile
          label="Totalling 100%"
          value={isPending ? '—' : balanced}
          caption={`of ${sets.length} ready to assign`}
        />
        <StatTile
          label="Department-scoped"
          value={isPending ? '—' : sets.filter((s) => s.departmentId !== null).length}
          caption={`${sets.filter((s) => s.departmentId === null).length} available org-wide`}
        />
      </div>

      {error && <p className="text-sm text-destructive">{error.message}</p>}

      <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
        <LibraryList
          items={sets.map((set) => ({
            id: set.id,
            name: set.name,
            // The job titles are on the row: a group that assigns itself on save is not
            // something you should have to open the editor to discover. Two names, then
            // a count — the whole list would push the weightage off a narrow row.
            subtitle: `${set.items.length} KRA${set.items.length === 1 ? '' : 's'} · ${set.totalWeightage}%${
              set.designationTitles.length > 0
                ? ` · ${set.designationTitles.slice(0, 2).join(', ')}${
                    set.designationTitles.length > 2
                      ? ` +${set.designationTitles.length - 2}`
                      : ''
                  }`
                : ''
            }`,
            canModify: perms.canModify(set),
            ownerNote: perms.ownerNote(set),
          }))}
          isPending={isPending}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onDelete={(item) => setDeleting(item)}
          searchPlaceholder="Find or create a group"
          emptyHint="No groups yet. Type a name to create one from the KRAs in your library, or bulk import a spreadsheet."
          onCreate={startNew}
          createLabel="Create group"
          bulkDelete={{
            noun: 'groups',
            description:
              'The KRAs inside them stay in the library. Employees already assigned from these groups keep the goals they were given; the groups just stop being assignable and stop auto-assigning to their job titles.',
            run: (id) => remove.mutateAsync(id),
          }}
        >
          <Button hint={"Create a new group of KRAs"} variant="outline" className="gap-2" data-guide="kra-group-new" onClick={() => startNew('')}>
            <Plus className="size-4" strokeWidth={2.4} />
            Create New KRA Group
          </Button>
          <BulkImportButton onClick={() => setImporting(true)}>Bulk import</BulkImportButton>
        </LibraryList>

        {editorOpen ? (
          // Remount on selection change: no effect syncing props into state, so a
          // background refetch cannot wipe unsaved edits.
          <KraSetEditor
            key={selectedId === NEW ? `new:${draftName}` : selectedId}
            kraSet={selected}
            initialName={draftName}
            onClose={() => setSelectedId(null)}
            onCreated={setSelectedId}
          />
        ) : (
          <Card>
            <CardContent className="py-20 text-center">
              <Layers className="mx-auto mb-3 size-6 text-muted-foreground" />
              <p className="text-sm font-medium">Pick a group to view or edit</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                A group is the mix of KRAs one job is judged on — ten KRAs at 30, 15, 10, 10, 10, 5,
                5, 5, 5 and 5 make up a Technical Architect. The weightages must total exactly 100%.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <KraSetImportDialog open={importing} onOpenChange={setImporting} />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
        title={`Delete the group "${deleting?.name ?? ''}"?`}
        description="The KRAs inside it are not affected — they stay in the library for other groups to use. Employees already assigned from this group keep the goals it gave them; deleting it only stops it being assigned again, and stops it auto-assigning to the job titles it is pinned to."
        confirmLabel="Delete group"
        destructive
        pending={remove.isPending}
        onConfirm={() => {
          const item = deleting;
          setDeleting(null);
          if (item) void onDelete(item);
        }}
      />
    </div>
  );
}

interface DraftItem {
  kraGroupId: string;
  weightage: number;
}

function KraSetEditor({
  kraSet,
  initialName,
  onClose,
  onCreated,
}: {
  kraSet: KraSet | null;
  /** Pre-filled name for a new group started from the search box. Ignored when editing. */
  initialName: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = React.useState(kraSet?.name ?? initialName);
  const [description, setDescription] = React.useState(kraSet?.description ?? '');
  const [departmentId, setDepartmentId] = React.useState(kraSet?.departmentId ?? ORG_WIDE);
  const [designationIds, setDesignationIds] = React.useState<string[]>(
    kraSet?.designationIds ?? [],
  );
  const [items, setItems] = React.useState<DraftItem[]>(
    kraSet?.items.map((i) => ({ kraGroupId: i.kraGroupId, weightage: i.weightage })) ?? [],
  );

  const { data: kraGroups } = useKraGroups();
  const { data: designations } = useDesignations();
  const { data: departmentTree } = useDepartments();
  const createKra = useCreateKraGroup();
  const departments = React.useMemo(
    () => flattenDepartments(departmentTree ?? []),
    [departmentTree],
  );

  const create = useCreateKraSet();
  const update = useUpdateKraSet();
  const saving = create.isPending || update.isPending;

  // Cached by react-query — the list this editor was opened from. Used only to mark job
  // titles another group already holds, so the picker says so instead of Save failing.
  const { data: allSets } = useKraSets();
  const takenBy = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const s of allSets ?? []) {
      if (s.id === kraSet?.id) continue;
      for (const id of s.designationIds) map.set(id, s.name);
    }
    return map;
  }, [allSets, kraSet?.id]);

  const chosenDesignations = React.useMemo(
    () => (designations ?? []).filter((d) => designationIds.includes(d.id)),
    [designations, designationIds],
  );
  const coveredHeadcount = chosenDesignations.reduce((sum, d) => sum + d.employeeCount, 0);
  // Anyone may read a group; only its author or HR may save a change to it.
  const perms = useKraPermissions();
  const canEdit = kraSet === null ? perms.canManage : perms.canModify(kraSet);

  // The same function the API rejects with, so the greyed-out Save and the 400 can
  // never disagree about what a valid group is.
  const validation = validateKraSet(items);
  const chosen = new Set(items.map((i) => i.kraGroupId));
  const available = (kraGroups ?? []).filter((k) => !chosen.has(k.id));

  /**
   * Designations that have no KRA yet, offered in the same dropdown as the real ones.
   *
   * Picking one creates that KRA and uses it, rather than sending the user off to the Add
   * KRA tab first. That is the whole point: the job titles already exist in Organization →
   * Designations, and retyping them into a second list is work the app can do.
   *
   * They are created lazily, on pick, so only the ones actually used become rows.
   */
  const uncovered = React.useMemo(() => {
    const covered = new Set((kraGroups ?? []).map((k) => k.name.trim().toLowerCase()));
    return (designations ?? [])
      .filter((d) => !covered.has(d.title.trim().toLowerCase()))
      .sort((a, b) => b.employeeCount - a.employeeCount || a.title.localeCompare(b.title));
  }, [kraGroups, designations]);

  const next = available[0];
  const canAddRow = next !== undefined || uncovered.length > 0;

  /**
   * KRAs in this group that have no goals under them.
   *
   * These are the ones that would take a share of the rating and measure nothing —
   * see the note in KraRow. Named rather than counted, because the fix is per KRA.
   */
  const emptyKras = React.useMemo(
    () =>
      items.flatMap((item) => {
        const kra = (kraGroups ?? []).find((k) => k.id === item.kraGroupId);
        return kra && kra.items.length === 0
          ? [{ name: kra.name, weightage: item.weightage }]
          : [];
      }),
    [items, kraGroups],
  );

  /**
   * Resolve a picked value to a real KRA id, creating the KRA if it was a name.
   *
   * Throws rather than swallowing, so the picker that called it can hold itself open
   * and say so. A create that failed silently and closed the dropdown would leave the
   * row pointing at nothing, and the next thing the user sees is Save refusing.
   */
  async function resolveKra(value: string): Promise<string> {
    if (!value.startsWith(DESIGNATION_PREFIX)) return value;
    const title = value.slice(DESIGNATION_PREFIX.length);
    const created = await createKra.mutateAsync({ name: title, items: [] });
    toast.success(`KRA "${title}" created`, {
      description:
        'It has no goals yet, so it would measure nothing. The row now links to where you add them.',
    });
    return created.id;
  }

  /**
   * Why Save is disabled, in the order the user hits them.
   *
   * A disabled button with no reason beside it is a dead end: an empty Group name looks
   * filled in because the placeholder is a plausible name, so Save just stopped working
   * and the group was never created. Never disable silently.
   */
  const blocker: string | null = !canEdit
    ? `${kraSet?.createdByName ?? 'HR'} created this group — ask them or HR to change it.`
    : name.trim() === ''
      ? 'Give the group a name.'
      : (validation.issues[0]?.message ?? null);

  function setItem(index: number, patch: Partial<DraftItem>) {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const firstIssue = validation.issues[0];
    if (firstIssue) {
      toast.error(firstIssue.message);
      return;
    }

    const body = {
      name: name.trim(),
      description: description.trim() || null,
      departmentId: departmentId === ORG_WIDE ? null : departmentId,
      designationIds,
      items,
    };

    try {
      // Both report what the save assigned, so a pinned group never succeeds silently
      // while the people it was meant for got nothing.
      if (kraSet) {
        const saved = await update.mutateAsync({ id: kraSet.id, ...body });
        toast.success(`Updated "${body.name}"`, {
          description: autoAssignNote(saved.autoAssigned),
        });
      } else {
        const created = await create.mutateAsync(body);
        toast.success(`Created "${body.name}"`, {
          description: autoAssignNote(created.autoAssigned),
        });
        onCreated(created.id);
      }
    } catch (e) {
      toast.error('Could not save that group', { description: err(e) });
    }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <form onSubmit={onSubmit} className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="grid min-w-64 flex-1 gap-2">
              <Label htmlFor="setName">Group name</Label>
              {/* Plain text — the search box that opens this editor already prefilled
                  it. See the note on the KRA name field. */}
              <Input
                id="setName"
                data-guide="kra-group-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Technical Architect"
                required
              />
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total weightage</p>
              <Badge
                // Blue while it is still being built — routine progress, not a warning.
                // Green only at exactly 100, red only when it can no longer be valid.
                variant={
                  validation.total === 100
                    ? 'soft-success'
                    : validation.total > 100
                      ? 'soft-destructive'
                      : 'info'
                }
                className={`${PILL_CLASS} mt-1`}
              >
                {validation.total}%
              </Badge>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="setDepartment">Available to</Label>
              <Combobox
                id="setDepartment"
                value={departmentId}
                onChange={setDepartmentId}
                options={[
                  { value: ORG_WIDE, label: 'Everyone' },
                  ...departments.map((d) => ({ value: d.id, label: d.label })),
                ]}
                searchPlaceholder="Search departments"
                emptyText="No department matches"
              />
              <p className="text-xs text-muted-foreground">
                Who sees this group in their picker. Does not assign anything.
              </p>
            </div>

            <div className="grid gap-2" data-guide="kra-group-titles">
              <Label htmlFor="setDesignation">Assign automatically to</Label>
              {/* Single-select picker that appends, plus chips — the same shape the KRA
                  rows use, rather than a second multi-select widget to maintain. Each
                  pick adds one title; the X on a chip takes it back off. */}
              <Combobox
                id="setDesignation"
                value={null}
                placeholder="Add a job title"
                onChange={(id) => setDesignationIds((prev) => [...prev, id])}
                options={(designations ?? [])
                  .filter((d) => !designationIds.includes(d.id))
                  .map((d) => ({
                    value: d.id,
                    label: d.title,
                    // Names the group already holding it rather than letting the user
                    // pick it and hit a 400 on Save. One job title, one group.
                    hint: takenBy.get(d.id)
                      ? `on "${takenBy.get(d.id)}"`
                      : `${d.employeeCount} ${d.employeeCount === 1 ? 'person' : 'people'}`,
                    disabled: takenBy.has(d.id),
                    group: takenBy.has(d.id) ? 'Already on another group' : 'Job titles',
                  }))}
                searchPlaceholder="Search job titles"
                emptyText="No job title matches"
              />

              {chosenDesignations.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {chosenDesignations.map((d) => (
                    <Badge key={d.id} variant="soft-muted" className={`${PILL_CLASS} gap-1 pr-1`}>
                      {d.title}
                      <span className="text-muted-foreground">{d.employeeCount}</span>
                      <Hint label={`Remove ${d.title}`}><button
                        type="button"
                        aria-label={`Remove ${d.title}`}
                        className="rounded-sm p-0.5 hover:bg-background/60"
                        onClick={() =>
                          setDesignationIds((prev) => prev.filter((id) => id !== d.id))
                        }
                      >
                        <X className="size-3" />
                      </button></Hint>
                    </Badge>
                  ))}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                {chosenDesignations.length === 0
                  ? 'Add the job titles this group is for, and saving builds the goal set for everyone holding them.'
                  : `Saving assigns this group to all ${coveredHeadcount} ${
                      coveredHeadcount === 1 ? 'person' : 'people'
                    } across ${chosenDesignations.length} job title${
                      chosenDesignations.length === 1 ? '' : 's'
                    }. Anyone who already has goals is left untouched.`}
              </p>
            </div>

            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="setDescription">Description</Label>
              <Input
                id="setDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this group is for"
              />
            </div>
          </div>

          <div className="space-y-4 border-t pt-5" data-guide="kra-group-kras">
            <Label>KRAs in this group</Label>

            {items.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Nothing yet. Add KRAs until the weightages total 100%.
              </p>
            )}

            {items.map((item, index) => (
              <KraRow
                key={index}
                item={item}
                index={index}
                // Already-chosen KRAs are hidden from the other rows rather than
                // rejected after the fact: the same KRA twice at 25+25 is a slip.
                options={available}
                uncovered={uncovered}
                allGroups={kraGroups ?? []}
                busy={createKra.isPending}
                onPick={(value) => setItem(index, { kraGroupId: value })}
                // Awaited by the picker, which stays open showing "Creating…" and
                // reports a failure in place rather than closing on a KRA that is not
                // there. The row only moves once the id is real.
                onCreate={async (title) => {
                  setItem(index, { kraGroupId: await resolveKra(`${DESIGNATION_PREFIX}${title}`) });
                }}
                onChange={(patch) => setItem(index, patch)}
                onRemove={() => setItems((prev) => prev.filter((_, i) => i !== index))}
              />
            ))}

            <Button hint={"Add a KRA to this group"}
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={!canAddRow || createKra.isPending}
              onClick={() => {
                // Seed with whatever is left of 100 so a four-KRA group is four clicks,
                // not four clicks and four sliders.
                const weightage = Math.max(1, Math.min(100, validation.remaining));
                if (next) {
                  setItems((prev) => [...prev, { kraGroupId: next.id, weightage }]);
                  return;
                }
                // Nothing left in the library, but a designation still has no KRA:
                // create it and use it, rather than dead-ending on a disabled button.
                const title = uncovered[0]?.title;
                if (!title) return;
                void resolveKra(`${DESIGNATION_PREFIX}${title}`)
                  .then((id) => setItems((prev) => [...prev, { kraGroupId: id, weightage }]))
                  .catch((e: unknown) =>
                    toast.error(`Could not create the KRA "${title}"`, { description: err(e) }),
                  );
              }}
            >
              <Plus className="size-4" />
              Add KRA
            </Button>

            {!canAddRow && (
              <p className="text-xs text-muted-foreground">
                Every KRA and designation is already in this group.
              </p>
            )}

            {items.length > 0 && validation.total !== 100 && (
              <p className="text-xs text-muted-foreground">
                {validation.remaining > 0
                  ? `${validation.remaining}% left to allocate — a group saves only at exactly 100%.`
                  : `${Math.abs(validation.remaining)}% over — trim a KRA before saving.`}
              </p>
            )}

            {validation.tooFewToAccept && (
              <p className="text-xs text-muted-foreground">
                Only {items.length} KRAs. This group can be saved, but an employee&apos;s goal set
                needs at least {MIN_ORG_GOALS} goals before it can be published.
              </p>
            )}

            {/* Said once more at the bottom, beside Save, because saving a pinned group
                assigns it to everyone holding its job titles straight away — and the
                empty KRAs go out with it. */}
            {emptyKras.length > 0 && (
              <p className="text-xs text-warning">
                {emptyKras.map((k) => `"${k.name}"`).join(', ')}{' '}
                {emptyKras.length === 1 ? 'has' : 'have'} no goals, so{' '}
                {emptyKras.reduce((sum, k) => sum + k.weightage, 0)}% of this group would
                measure nothing. Add their goals before assigning it.
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
            {blocker && <p className="mr-auto text-xs text-muted-foreground">{blocker}</p>}
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" data-guide="kra-group-save" disabled={saving || blocker !== null}>
              {saving ? 'Saving…' : kraSet ? 'Save changes' : 'Create group'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function KraRow({
  item,
  index,
  options,
  uncovered,
  allGroups,
  busy,
  onPick,
  onCreate,
  onChange,
  onRemove,
}: {
  item: DraftItem;
  index: number;
  options: KraGroup[];
  /** Designations with no KRA yet. Picking one creates it. */
  uncovered: Designation[];
  allGroups: KraGroup[];
  busy: boolean;
  onPick: (value: string) => void;
  /** Create a KRA by name and use it. Rejects if the API refuses. */
  onCreate: (name: string) => Promise<void>;
  onChange: (patch: Partial<DraftItem>) => void;
  onRemove: () => void;
}) {
  // This row's own KRA is filtered out of `options`, so put it back — otherwise the
  // picker renders blank against a value it cannot find.
  const mine = allGroups.find((k) => k.id === item.kraGroupId);
  const choices = mine ? [mine, ...options] : options;
  /**
   * A KRA with no goals carries weight and rates nothing.
   *
   * Not cosmetic: assign copies the KRA's goals into `Kra` rows, so an empty one becomes
   * a Goal at (say) 60% with nothing underneath. At appraisal it produces no rating,
   * drops out of `weightedOverall`, and the employee is silently rated on the other 40%
   * renormalised to 100. Nobody sees a number go missing. Saving a pinned group assigns
   * it immediately, so this is the last place to catch it.
   */
  const empty = mine !== undefined && mine.items.length === 0;

  return (
    <div className="grid gap-3 rounded-md border p-4 sm:grid-cols-[1fr_auto]">
      <div className="grid gap-2">
        <Label htmlFor={`kra${index}`} className="text-xs">
          Choose KRA
        </Label>
        <Combobox
          id={`kra${index}`}
          value={item.kraGroupId}
          // A designation row is a name, not an id: picking one goes down the same
          // create path as typing it, and the picker waits for the KRA to exist.
          onChange={(value) =>
            value.startsWith(DESIGNATION_PREFIX)
              ? onCreate(value.slice(DESIGNATION_PREFIX.length))
              : onPick(value)
          }
          disabled={busy}
          placeholder="Pick a KRA"
          searchPlaceholder="Find or create a KRA"
          emptyText="No KRA matches"
          options={[
            ...choices.map((k) => ({
              value: k.id,
              label: k.name,
              hint: `${k.items.length} goal${k.items.length === 1 ? '' : 's'}`,
            })),
            ...uncovered.map((designation) => ({
              value: `${DESIGNATION_PREFIX}${designation.title}`,
              label: designation.title,
              hint: designation.employeeCount > 0 ? `${designation.employeeCount}` : undefined,
              group: 'From designations — creates the KRA',
            })),
          ]}
          // Typing a name that is neither an existing KRA nor a designation creates it
          // through the same path a designation takes, so "not in the list yet" is never
          // a reason to leave this screen.
          onCreate={onCreate}
          createLabel="Create KRA"
        />
        {empty && (
          <p className="text-xs text-warning">
            No goals in this KRA yet — it would take {item.weightage}% of the rating and
            measure nothing.{' '}
            {/* Search-only `to`, so this works from both /team/kra and /hr/kra-groups. */}
            <Link
              to={{ search: `?tab=add&kra=${item.kraGroupId}` }}
              className="font-medium underline underline-offset-2"
            >
              Add its goals
            </Link>
          </p>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor={`weightage${index}`} className="text-xs">
          Weightage
        </Label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={1}
            max={100}
            step={1}
            className="h-1.5 w-32 accent-primary sm:w-40"
            aria-label={`Weightage for KRA ${index + 1}`}
            value={item.weightage}
            onChange={(e) => onChange({ weightage: Number(e.target.value) })}
          />
          <Input
            id={`weightage${index}`}
            type="number"
            min={1}
            max={100}
            step={1}
            className="w-20"
            value={item.weightage}
            // Whole numbers only: 33.33 three times totals 99.99 and would fail an
            // exact-100 check for a reason nobody can see or fix.
            onChange={(e) => onChange({ weightage: Math.round(Number(e.target.value) || 0) })}
          />
          <span className="text-xs text-muted-foreground">%</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove KRA ${index + 1}`}
            onClick={onRemove}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Bulk import groups — one row per KRA, repeating the group name.
 *
 * The sheet REFERENCES KRAs by name and never creates them. A group sheet that
 * authored the KRAs it mentioned would fill the library with empty stencils that rate
 * nothing, and the person importing would then have to find and delete the ones they
 * never meant to make. So an unknown name blocks that group and says which name it is,
 * rather than being quietly invented.
 *
 * The exact-100 rule blocks here too, for the same reason Save is disabled on it: a
 * group saved at 95% assigns a goal set nobody can accept, and the missing 5% would
 * only surface at assignment time.
 */
function KraSetImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: kraGroups } = useKraGroups();
  const { data: sets } = useKraSets();
  const create = useCreateKraSet();

  /** Name → id, case-insensitive, the same match the KRA import groups rows by. */
  const byName = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const kra of kraGroups ?? []) map.set(kra.name.trim().toLowerCase(), kra.id);
    return map;
  }, [kraGroups]);

  const taken = React.useMemo(
    () => new Set((sets ?? []).map((s) => s.name.trim().toLowerCase())),
    [sets],
  );

  function resolve(set: ImportedKraSet) {
    const missing = set.items.filter((i) => !byName.has(i.kraName.trim().toLowerCase()));
    const items = set.items.map((i) => ({
      kraGroupId: byName.get(i.kraName.trim().toLowerCase()) ?? '',
      weightage: i.weightage,
    }));
    return { missing, items };
  }

  return (
    <BulkImportDialog<ImportedKraSet>
      open={open}
      onOpenChange={onOpenChange}
      title="Bulk import groups"
      description="One row per KRA, repeating the group name. The KRAs must already be in the library — a group only weights them. Import only creates; a group that already exists is left exactly as it is."
      templateHref="/kra-group-import-sample.csv"
      noun="group"
      parse={(csv) => {
        const result = parseKraSetCsv(csv);
        return { rows: result.sets, errors: result.errors };
      }}
      summarise={(set) => {
        const total = set.items.reduce((sum, i) => sum + i.weightage, 0);
        const text = `${set.items.length} KRA${set.items.length === 1 ? '' : 's'} · ${total}%`;
        if (taken.has(set.name.trim().toLowerCase())) {
          return { text, blocker: 'Already in the library — left untouched.' };
        }
        const { missing } = resolve(set);
        if (missing.length > 0) {
          return {
            text,
            blocker: `Not in the KRA library: ${missing.map((i) => i.kraName).join(', ')}. Add ${
              missing.length === 1 ? 'it' : 'them'
            } under Add KRA first.`,
          };
        }
        // The same rule validateKraSet applies, phrased the way the editor does.
        const issue = validateKraSet(resolve(set).items).issues[0];
        return issue ? { text, blocker: issue.message } : { text };
      }}
      create={(set) => create.mutateAsync({ name: set.name, items: resolve(set).items })}
    />
  );
}
